import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, DatePicker, Form, Input, Row, Select, Space, theme } from 'antd';
import { EnumDeviceSearchType, IEventSearchParams } from '../../../common/models';
import { getEnumEntries } from '../../../common/utils';

interface IEventSearchFormProps {
  searchParams: IEventSearchParams;
  customers: any[];
  onSearch: (searchParams: IEventSearchParams) => void;
  onExport: (searchParams: IEventSearchParams) => void;
}

const { RangePicker } = DatePicker;

const EventSearchForm: React.FC<IEventSearchFormProps> = ({
  searchParams,
  customers,
  onSearch,
  onExport,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const onSearchRef = useRef(onSearch);
  const [currSearchType, setCurrSearchType] = useState(searchParams.searchType);

  const updateFormFields = useCallback((customers: any[]) => {
    if (customers.length === 0) return;
    form.setFieldsValue({
      searchType: searchParams.searchType,
      keywords: searchParams.searchType === EnumDeviceSearchType.customerName && !searchParams.keywords ? customers[0].Name : searchParams.keywords,
      timeRange: searchParams.timeRange,
    }); // This will not trigger handleSearchTypeChange
  }, [form, searchParams]);

  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    updateFormFields(customers);
  }, [updateFormFields, customers, searchParams]);

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
    borderRadius: token.borderRadiusLG,
  };

  const handleSearchTypeChange = (newSearchType: EnumDeviceSearchType) => {
    form.setFieldsValue({ keywords: '' });
    setCurrSearchType(newSearchType);
  };

  const getFields = () => {
    const fields = [
      <Col
        xs={24}
        sm={24}
        md={12}
        xl={6}
        key='type'
        style={{ padding: 10}}
      >
        <Form.Item
          name='searchType'
          initialValue={searchParams.searchType}
          style={{ flex: 1, marginBottom: 0 }}
        >
          <Select
            options={getEnumEntries(EnumDeviceSearchType)}
            onChange={handleSearchTypeChange}
            style={{ width: '100%' }}
          />
        </Form.Item>

      </Col>,
      <Col
        xs={24}
        sm={24}
        md={12}
        xl={6}
        key='search'
        style={{ padding: 10}}
      >
          {
            currSearchType === EnumDeviceSearchType.customerName ? (
              <Form.Item
                name='keywords'
                initialValue={searchParams.keywords}
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: `Please input and select ${currSearchType}` }]}
              >
                <Select
                  showSearch
                  placeholder="Search to Select"
                  optionFilterProp="label"
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? '').toLowerCase().localeCompare((optionB?.label ?? '').toLowerCase())
                  }
                  options={customers.map((customer) => ({
                    value: customer.Name,
                    label: customer.Name,
                  }))}
                  style={{ flex: 1 }}
                />
              </Form.Item>
            ) : (
              <Form.Item
                name='keywords'
                initialValue={searchParams.keywords}
                style={{ flex: 1, marginBottom: 0 }}
                rules={[{ required: true, message: `Please input ${currSearchType}` }]}
              >
                <Input
                  allowClear
                />
              </Form.Item>
            )
          }
      </Col>,
      <Col
        xs={24}
        sm={24}
        md={12}
        xl={6}
        key='timeRange'
        style={{ padding: 10}}
      >
        <Space>
          <Form.Item
            name='timeRange'
            style={{ marginBottom: 0 }}
            rules={[{ required: true, message: '' }]}
          >
            <RangePicker
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              onChange={(value, dateString) => {
              }}
            />
          </Form.Item>
          <Button
            onClick={() => {
              form.resetFields(['timeRange']);
            }}
          >
            Clear
          </Button>
        </Space>
      </Col>,
      <Col
        xs={24}
        sm={24}
        md={12}
        xl={6}
        key='searchButton'
        style={{ padding: 10}}
      >
        <Space size="small">
          <Button
            type="primary"
            onClick={async () => {
              try {
                // Clear previous validation errors but keep the values
                form.setFields([{name: 'searchType', errors: []}]);
                await form.validateFields(['keywords']);
                onSearch({ searchType: searchParams.searchType, ...form.getFieldsValue() });
              } catch {}
            }}
          >
            Search
          </Button>
          <Button
            type="primary"
            onClick={async () => {
              try {
                await form.validateFields(['keywords', 'timeRange']);
                onExport({ searchType: searchParams.searchType, ...form.getFieldsValue() });
              } catch {}
            }}
          >
            Export to CSV
          </Button>
        </Space>
      </Col>
    ];
    return fields;
  };

  return (
    <Form
      form={form}
      name="advanced_search"
      style={formStyle}
    >
      <Row gutter={24} wrap style={{ marginLeft: 0, marginRight: 0 }}>{getFields()}</Row>
    </Form>
  );
};

export default EventSearchForm;